import { LayoutList, Network } from 'lucide-react';
import { useMemo, useState } from 'react';
import { NodeThumb } from '@/components/value-chain/explorer/NodeThumb';
import {
  importanceFill,
  importanceFromRank,
  importanceTone,
  shortName,
} from '@/components/value-chain/nodeVisuals';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import { childrenOf } from '@/components/value-chain/treeIndex';
import type { ValueChainNode } from '@/types/valueChain';

type SortKey = 'importance' | 'name' | 'nodes';

type Props = {
  parent: ValueChainNode;
  index: TreeIndex;
  selectedId: string;
  onSelect: (id: string) => void;
  onDrill: (id: string) => void;
};

export function ChildrenPanel({ parent, index, selectedId, onSelect, onDrill }: Props) {
  const [mode, setMode] = useState<'tree' | 'list'>('tree');
  const [sort, setSort] = useState<SortKey>('importance');

  const rows = useMemo(() => {
    const children = childrenOf(index, parent.id);
    const decorated = children.map((node) => ({
      node,
      rank: index.inboundBom.get(node.id)?.rank ?? Number.POSITIVE_INFINITY,
      size: index.subtreeSize.get(node.id) ?? 1,
    }));
    decorated.sort((a, b) => {
      if (sort === 'name') return a.node.name.localeCompare(b.node.name);
      if (sort === 'nodes') return b.size - a.size;
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.node.name.localeCompare(b.node.name);
    });
    return decorated;
  }, [index, parent.id, sort]);

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-gray-900">
            Children of {shortName(parent.name)}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-[12px] text-gray-500">
            Sort by
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as SortKey)}
              className="rounded-md border border-gray-200 bg-white px-1.5 py-1 text-[12px] text-gray-700 outline-none"
            >
              <option value="importance">Importance</option>
              <option value="nodes">Node count</option>
              <option value="name">Name</option>
            </select>
          </label>
          <div className="flex rounded-lg border border-gray-200 p-0.5">
            <button
              type="button"
              onClick={() => setMode('tree')}
              className={`rounded-md p-1 ${mode === 'tree' ? 'bg-gray-900 text-white' : 'text-gray-500'}`}
              aria-label="Tree"
            >
              <Network className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setMode('list')}
              className={`rounded-md p-1 ${mode === 'list' ? 'bg-gray-900 text-white' : 'text-gray-500'}`}
              aria-label="List"
            >
              <LayoutList className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {rows.length === 0 ? (
          <p className="px-3 py-6 text-sm text-gray-500">No mapped children under this node.</p>
        ) : (
          rows.map(({ node, rank, size }) => {
            const active = node.id === selectedId;
            const level = importanceFromRank(Number.isFinite(rank) ? rank : null);
            const childCount = (index.children.get(node.id) ?? []).length;
            return (
              <button
                key={node.id}
                type="button"
                onClick={() => onSelect(node.id)}
                onDoubleClick={() => onDrill(node.id)}
                className={`mb-1 flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left ${
                  active ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'
                }`}
              >
                <NodeThumb node={node} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-gray-900">
                    {shortName(node.name)}
                  </span>
                  <span className="block truncate text-[11px] text-gray-400">
                    {node.type}
                    {mode === 'list' ? ` · ${size.toLocaleString()} nodes` : ''}
                  </span>
                </span>
                {level ? (
                  <span className={`flex items-center gap-2 text-[12px] font-medium ${importanceTone(level)}`}>
                    {level}
                    <span className="flex w-10 justify-end">
                      <span className={`h-1.5 rounded-full ${importanceFill(level)}`} style={{ width: `${Math.max(18, (5 - Math.min(rank, 5)) * 8)}px` }} />
                    </span>
                  </span>
                ) : childCount > 0 ? (
                  <span className="text-[11px] text-gray-400">{childCount} parts</span>
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </section>
  );
}
